'use client'

import { useId } from 'react'

import { useTrackElementInteracted } from '@nwwp/segment-analytics'

const SOCIAL_LINKS = [
  {
    name: 'Spotify',
    href: 'https://open.spotify.com/show/033dmsa1E3QkFuPsIcQ5hr',
    icon: SpotifyIcon,
  },
  {
    name: 'Apple Podcasts',
    href: 'https://podcasts.apple.com/us/podcast/your-next-dollar-money-management-for-high-earners/id1896598041',
    icon: ApplePodcastsIcon,
  },
  {
    name: 'YouTube',
    href: 'https://www.youtube.com/@NerdWalletWealthPartners',
    icon: YouTubeIcon,
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/nerdwalletwealthpartners/',
    icon: InstagramIcon,
  },
  {
    name: 'TikTok',
    href: 'https://www.tiktok.com/@nerdwalletwealthpartners',
    icon: TikTokIcon,
  },
] as const

function SpotifyIcon({ uid }: { uid: string }) {
  const gradientId = `${uid}-spotify-bg`
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 46.9845 46.9984"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M23.5458 0H23.4388C10.4939 0 0 10.497 0 23.4457V23.5527C0 36.5014 10.4939 46.9984 23.4388 46.9984H23.5458C36.4906 46.9984 46.9845 36.5014 46.9845 23.5527V23.4457C46.9845 10.497 36.4906 0 23.5458 0Z"
        fill={`url(#${gradientId})`}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M23.5 7C13.8405 7 6 14.6158 6 23.9991C6 33.4029 13.8405 41 23.5 41C33.1801 41 40.9999 33.3825 40.9999 23.9991C41.0241 14.6158 33.1801 7 23.5 7ZM31.53 31.5348C31.3753 31.7769 31.1306 31.9489 30.8487 32.0134C30.5669 32.0779 30.2708 32.0298 30.0246 31.8794C25.9045 29.4432 20.7406 28.8938 14.6346 30.2348C14.049 30.3559 13.4633 30.013 13.3393 29.4432C13.3084 29.3075 13.3051 29.167 13.3294 29.03C13.3537 28.893 13.4053 28.7622 13.4811 28.645C13.5569 28.5279 13.6554 28.4269 13.7709 28.3477C13.8864 28.2686 14.0166 28.213 14.154 28.1841C20.8233 26.6998 26.5539 27.3311 31.1545 30.0727C31.6971 30.3764 31.8641 31.0281 31.53 31.5348ZM33.683 26.9046C33.488 27.2052 33.1832 27.4195 32.8324 27.5028C32.4816 27.5861 32.1118 27.5319 31.8004 27.3515C27.0981 24.528 19.9259 23.7159 14.3642 25.3606C13.6321 25.5636 12.8794 25.178 12.6693 24.4871C12.6179 24.3193 12.6011 24.143 12.6198 23.9687C12.6385 23.7943 12.6923 23.6255 12.7781 23.4721C12.8639 23.3188 12.9798 23.184 13.1191 23.0759C13.2584 22.9677 13.4182 22.8883 13.5891 22.8424C19.9449 20.9726 27.8509 21.8666 33.2645 25.0961C33.8708 25.4612 34.0809 26.2733 33.683 26.9046ZM33.8725 22.0696C28.2246 18.823 18.92 18.5159 13.5271 20.1025C13.1136 20.2263 12.6677 20.1849 12.2846 19.9872C11.9016 19.7896 11.6119 19.4513 11.4774 19.0448C11.2069 18.2122 11.7082 17.3199 12.5659 17.0555C18.7547 15.2266 29.0411 15.5934 35.5226 19.3297C35.7055 19.434 35.8656 19.5734 35.9934 19.7398C36.1212 19.9062 36.2141 20.0962 36.2667 20.2987C36.3194 20.5012 36.3306 20.712 36.2999 20.9189C36.2691 21.1257 36.1969 21.3244 36.0876 21.5032C35.9806 21.6857 35.8382 21.8453 35.6685 21.9729C35.4988 22.1004 35.3053 22.1933 35.099 22.2461C34.8928 22.299 34.678 22.3108 34.4672 22.2807C34.2564 22.2507 34.0536 22.1795 33.8708 22.0713L33.8725 22.0696Z"
        fill="black"
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="23.4923"
          y1="36.6291"
          x2="23.4923"
          y2="42.3651"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="#D7D7D7" />
        </linearGradient>
      </defs>
    </svg>
  )
}

function ApplePodcastsIcon({ uid }: { uid: string }) {
  const gradientId = `${uid}-apple-bg`
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 46.9845 46.9984"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M23.5458 0H23.4388C10.4939 0 0 10.497 0 23.4457V23.5527C0 36.5014 10.4939 46.9984 23.4388 46.9984H23.5458C36.4906 46.9984 46.9845 36.5014 46.9845 23.5527V23.4457C46.9845 10.497 36.4906 0 23.5458 0Z"
        fill={`url(#${gradientId})`}
      />
      <path
        d="M20.2638 28.9991C20.2638 27.1991 22.0188 26.7491 23.2638 26.7491C24.5088 26.7491 26.2638 27.1991 26.2638 28.9991C26.2638 32.7491 26.2638 39.4991 23.2638 39.4991C20.2638 39.4991 20.2638 32.7491 20.2638 28.9991Z"
        stroke="black"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M28.5138 35.4941C33.9438 33.3341 37.5138 28.0841 37.5138 22.2491C37.5138 14.3741 31.1388 7.99915 23.2638 7.99915C15.3888 7.99915 9.01382 14.3741 9.01382 22.2491C9.01382 28.2641 12.7338 33.4091 18.0138 35.4941"
        stroke="black"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M29.2638 28.9541C32.9688 25.6391 33.2838 19.9541 29.9688 16.2491C26.6538 12.5441 20.9688 12.2291 17.2638 15.5441C13.5588 18.8591 13.2438 24.5441 16.5588 28.2491C16.7838 28.5041 17.0238 28.7291 17.2638 28.9541"
        stroke="black"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M20.2638 21.4991C20.2638 23.1491 21.6138 24.4991 23.2638 24.4991C24.9138 24.4991 26.2638 23.1491 26.2638 21.4991C26.2638 19.8491 24.9138 18.4991 23.2638 18.4991C21.6138 18.4991 20.2638 19.8491 20.2638 21.4991Z"
        stroke="black"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="23.4923"
          y1="36.6291"
          x2="23.4923"
          y2="42.3651"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="#D7D7D7" />
        </linearGradient>
      </defs>
    </svg>
  )
}

function YouTubeIcon({ uid }: { uid: string }) {
  const gradientId = `${uid}-youtube-bg`
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 46.8512 46.865"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M46.8512 23.4325C46.8512 10.4911 36.3632 0 23.4256 0C10.488 0 0 10.4911 0 23.4325C0 36.3739 10.488 46.865 23.4256 46.865C36.3632 46.865 46.8512 36.3739 46.8512 23.4325Z"
        fill={`url(#${gradientId})`}
      />
      <path
        d="M38.176 18.5768C37.9917 16.7969 37.595 14.8292 36.1349 13.7952C35.004 12.9933 33.5176 12.9636 32.1299 12.9653C29.1966 12.9653 26.2616 12.9702 23.3283 12.9719C20.5068 12.9752 17.6854 12.9768 14.864 12.9801C13.6854 12.9801 12.5397 12.8895 11.4451 13.4C10.5052 13.838 9.76935 14.6712 9.32655 15.5982C8.71256 16.8875 8.58417 18.348 8.51009 19.7739C8.37347 22.3706 8.38828 24.9738 8.55125 27.5688C8.67141 29.4624 8.97593 31.5552 10.4393 32.7621C11.7364 33.8308 13.5669 33.8835 15.2492 33.8851C20.5891 33.89 25.9307 33.895 31.2723 33.8983C31.9571 33.8999 32.6715 33.8868 33.3694 33.811C34.7423 33.6628 36.0509 33.2693 36.9332 32.2517C37.8238 31.2259 38.0526 29.7983 38.1876 28.4464C38.5168 25.1665 38.5135 21.8552 38.176 18.5768ZM20.2402 28.0299V18.8337L28.2007 23.431L20.2402 28.0299Z"
        fill="black"
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="23.4256"
          y1="36.5252"
          x2="23.4256"
          y2="42.2449"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="#D7D7D7" />
        </linearGradient>
      </defs>
    </svg>
  )
}

function InstagramIcon({ uid }: { uid: string }) {
  const gradientId = `${uid}-instagram-bg`
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 46.9862 46.9984"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M46.9829 23.5C46.9829 35.3686 38.1878 45.1805 26.7622 46.7728C25.6939 46.921 24.6009 46.9984 23.4914 46.9984C22.2108 46.9984 20.9532 46.8963 19.7285 46.6987C8.54324 44.8973 0 35.1957 0 23.5C0 10.5216 10.5186 0 23.4931 0C36.4676 0 46.9862 10.5216 46.9862 23.5H46.9829Z"
        fill={`url(#${gradientId})`}
      />
      <path
        d="M30.8671 8.99915H16.3287C12.3123 8.99915 9.04477 12.2676 9.04477 16.2853V29.5896C9.04477 33.6073 12.3123 36.8758 16.3287 36.8758H30.8671C34.8835 36.8758 38.151 33.6073 38.151 29.5896V16.2853C38.151 12.2676 34.8835 8.99915 30.8671 8.99915ZM11.6143 16.2853C11.6143 13.6853 13.7296 11.5695 16.3287 11.5695H30.8671C33.4663 11.5695 35.5815 13.6853 35.5815 16.2853V29.5896C35.5815 32.1896 33.4663 34.3055 30.8671 34.3055H16.3287C13.7296 34.3055 11.6143 32.1896 11.6143 29.5896V16.2853Z"
        fill="black"
      />
      <path
        d="M23.5976 29.7132C27.3326 29.7132 30.3729 26.6736 30.3729 22.9358C30.3729 19.1981 27.3342 16.1585 23.5976 16.1585C19.861 16.1585 16.8223 19.1981 16.8223 22.9358C16.8223 26.6736 19.861 29.7132 23.5976 29.7132ZM23.5976 18.7305C25.917 18.7305 27.8034 20.6174 27.8034 22.9375C27.8034 25.2575 25.917 27.1445 23.5976 27.1445C21.2783 27.1445 19.3918 25.2575 19.3918 22.9375C19.3918 20.6174 21.2783 18.7305 23.5976 18.7305Z"
        fill="black"
      />
      <path
        d="M31.0002 17.2532C32.006 17.2532 32.8257 16.4349 32.8257 15.4272C32.8257 14.4194 32.0076 13.6011 31.0002 13.6011C29.9928 13.6011 29.1747 14.4194 29.1747 15.4272C29.1747 16.4349 29.9928 17.2532 31.0002 17.2532Z"
        fill="black"
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="23.4931"
          y1="36.6291"
          x2="23.4931"
          y2="42.3651"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="#D7D7D7" />
        </linearGradient>
      </defs>
    </svg>
  )
}

function TikTokIcon({ uid }: { uid: string }) {
  const gradientId = `${uid}-tiktok-bg`
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 46.9845 46.9984"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M23.5458 0H23.4388C10.4939 0 0 10.497 0 23.4457V23.5527C0 36.5014 10.4939 46.9984 23.4388 46.9984H23.5458C36.4906 46.9984 46.9845 36.5014 46.9845 23.5527V23.4457C46.9845 10.497 36.4906 0 23.5458 0Z"
        fill={`url(#${gradientId})`}
      />
      <path
        d="M34.8491 17.0025V21.44C34.0738 21.3643 33.0664 21.1881 31.952 20.7798C30.4968 20.2463 29.4137 19.5168 28.7042 18.9521V27.921L28.6861 27.893C28.6976 28.0708 28.7042 28.252 28.7042 28.4347C28.7042 32.8887 25.0812 36.5145 20.6268 36.5145C16.1725 36.5145 12.5494 32.8887 12.5494 28.4347C12.5494 23.9807 16.1725 20.3533 20.6268 20.3533C21.063 20.3533 21.491 20.3879 21.9091 20.4554V24.8287C21.5075 24.6855 21.0762 24.6081 20.6268 24.6081C18.5182 24.6081 16.8013 26.3238 16.8013 28.4347C16.8013 30.5456 18.5182 32.2614 20.6268 32.2614C22.7355 32.2614 24.4523 30.544 24.4523 28.4347C24.4523 28.3557 24.4507 28.2767 24.4458 28.1976V10.7685H28.8787C28.8952 11.144 28.91 11.5227 28.9264 11.8981C28.9561 12.6374 29.2194 13.3471 29.6787 13.9283C30.217 14.6117 31.012 15.4053 32.1281 16.0393C33.1734 16.6304 34.1544 16.8856 34.8491 17.0058V17.0025Z"
        fill="black"
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="23.4923"
          y1="36.6291"
          x2="23.4923"
          y2="42.3651"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="#D7D7D7" />
        </linearGradient>
      </defs>
    </svg>
  )
}

export function SocialIcons() {
  const uid = useId()
  const trackElementInteracted = useTrackElementInteracted()
  return (
    <div className="flex items-center gap-4">
      {SOCIAL_LINKS.map(({ name, href, icon: Icon }) => (
        <a
          key={name}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={name}
          onClick={() => trackElementInteracted({ element: name })}
        >
          <Icon uid={uid} />
        </a>
      ))}
    </div>
  )
}
